import { DOCS_HOME, DOCS_SUPPORT } from 'constants/Docs.constants';
import { Link } from 'react-router-dom';
import { PATHS } from 'routes/routes.constants';

import Clickable from '@polyai/ui/components/atoms/Clickable';
import {
  ChevronRightIcon,
  HelpIcon,
  LogoutIcon,
  SupportIcon,
  UserIcon,
} from '@polyai/ui/components/atoms/Icons';
import {
  ShadDropdownMenu,
  ShadDropdownMenuContent,
  ShadDropdownMenuItem,
  ShadDropdownMenuSeparator,
  ShadDropdownMenuTrigger,
} from '@polyai/ui/components/molecules/ShadDropdownMenu';
import {
  ShadSidebarMenuButton,
  ShadSidebarMenuItem,
} from '@polyai/ui/components/organisms/ShadSidebar';

import { useUserAccess } from 'hooks/useProjectResources/useUserAccess';

const truncateEmail = (email?: string) => {
  if (!email) return 'User';
  if (email.length < 20) return email;
  return `${email.slice(0, 17)}...`;
};

type IASidebarFooterProps = {
  userEmail?: string;
  logout?: () => void;
  onDropdownOpenChange?: (open: boolean) => void;
};

export const IASidebarFooter = ({
  userEmail,
  logout,
  onDropdownOpenChange,
}: IASidebarFooterProps) => {
  const { isInternalUser } = useUserAccess();

  return (
    <div className="mt-auto px-xs2 py-sm1">
      <ul className="flex flex-col gap-xs1">
        <ShadSidebarMenuItem>
          <ShadSidebarMenuButton
            render={<Clickable to={DOCS_HOME} />}
            tooltip="Help center"
          >
            <HelpIcon color="iconSecondary" size="md" />
            <span className="truncate group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:overflow-hidden">
              Help center
            </span>
          </ShadSidebarMenuButton>
        </ShadSidebarMenuItem>
        <ShadSidebarMenuItem>
          <ShadSidebarMenuButton
            render={<Clickable to={DOCS_SUPPORT} />}
            tooltip="Contact support"
          >
            <SupportIcon color="iconSecondary" size="md" />
            <span className="truncate group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:overflow-hidden">
              Contact support
            </span>
          </ShadSidebarMenuButton>
        </ShadSidebarMenuItem>
        <ShadSidebarMenuItem>
          <ShadDropdownMenu onOpenChange={onDropdownOpenChange}>
            <ShadDropdownMenuTrigger asChild>
              <ShadSidebarMenuButton
                tooltip="User account"
                type="button"
                data-test-id="ia-user-menu-button"
              >
                <UserIcon color="iconSecondary" size="md" />
                <span className="truncate group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:overflow-hidden">
                  {truncateEmail(userEmail)}
                </span>
                <ChevronRightIcon
                  className="ml-auto shrink-0 group-data-[collapsible=icon]:hidden"
                  color="iconSecondary"
                  size="sm"
                />
              </ShadSidebarMenuButton>
            </ShadDropdownMenuTrigger>
            <ShadDropdownMenuContent side="right" align="end" sideOffset={8}>
              <ShadDropdownMenuItem
                render={
                  <Link
                    data-test-id="ia-user-account-link"
                    to={PATHS.USER_ACCOUNT}
                  />
                }
              >
                Account
              </ShadDropdownMenuItem>
              {isInternalUser && (
                <ShadDropdownMenuItem
                  render={<a href={PATHS.ADMIN_PAGE} />}
                  data-test-id="ia-admin-link"
                >
                  Admin
                </ShadDropdownMenuItem>
              )}
              <ShadDropdownMenuSeparator />
              <ShadDropdownMenuItem
                data-test-id="ia-logout-btn"
                onClick={() => logout?.()}
              >
                Log out
                <LogoutIcon
                  className="ml-auto"
                  color="iconSecondary"
                  size="sm"
                />
              </ShadDropdownMenuItem>
            </ShadDropdownMenuContent>
          </ShadDropdownMenu>
        </ShadSidebarMenuItem>
      </ul>
    </div>
  );
};
